import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Position } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KontierungFelder } from "./KontierungFelder.js";
import type { KontierungWert } from "./KontierungFelder.js";
import { KONTIERUNG_TEXT } from "./kontierungTexts.js";
import { apiMeldung } from "./vorgangsdetail.js";

/**
 * DIE KONTIERUNG EINER POSITION AN DER FESTGESCHRIEBENEN RECHNUNG (Plan P10, Nachtrag Task 18; Lieferanten Aufgabe 22):
 * Die Positionen eines festgeschriebenen Belegs stehen lesend in einer Tabelle (nicht im Positionseditor) — ihre
 * Kontierung bleibt bis zur DATEV-Übergabe über den eigenen Kontierungsweg änderbar (`setInvoiceItemAssignment`, dreht
 * nur die Positionsversion). Weil der Beleg selbst feststeht, ist das eine direkte Aktion: Die Wahl fragt gestapelt nach
 * (K2 aus Review 18 — am Entwurf und am Auftrag wirkt sie sofort wie jede Positionsänderung). Nach der Übergabe
 * (`locked`) stehen die Felder lesend da. Ein fremder Stand (409) lädt die Positionen neu (`onStale`).
 *
 * `lists` sind die einmal je Beleg geteilten Auswahllisten (`sharedAssignmentLists`); den Hinweis auf die Folgestufe
 * nennt der Beleg einmal unter der Tabelle.
 */
export function InvoiceItemAssignment(p: {
  item: Position; customerId: string; invoiceId: string; companyId: string; locked: boolean;
  lists: Pick<ApiClient, "getBookingAccounts" | "getCostCentres">; client: Pick<ApiClient, "setInvoiceItemAssignment">;
  onChanged: (item: Position) => void; onStale: () => void; nummer: number;
}): ReactElement {
  const { bestaetigen } = useBestaetigung();
  const [notice, setNotice] = useState<{ art: "warnung" | "fehler"; text: string } | null>(null);

  async function assign(wert: KontierungWert): Promise<void> {
    if (!(await bestaetigen({
      titel: KONTIERUNG_TEXT.invoiceConfirmTitle, text: KONTIERUNG_TEXT.invoiceConfirmText(p.item.name),
      bestaetigenText: KONTIERUNG_TEXT.invoiceConfirmButton,
    }))) return;
    setNotice(null);
    try {
      p.onChanged(await p.client.setInvoiceItemAssignment(p.customerId, p.invoiceId, p.item.id,
        { version: p.item.version, ...wert }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === "VERSIONSKONFLIKT") {
        setNotice({ art: "warnung", text: KONTIERUNG_TEXT.invoiceConflict });
        p.onStale();
        return;
      }
      setNotice({ art: "fehler", text: apiMeldung(e, KONTIERUNG_TEXT.invoiceFailed) });
    }
  }

  return (
    <div className="krg__assignment" role="group" aria-label={KONTIERUNG_TEXT.row(p.nummer)}>
      {notice !== null && <Hinweis art={notice.art}>{notice.text}</Hinweis>}
      <KontierungFelder point="INVOICE_ITEM" companyId={p.companyId} client={p.lists} hint={false} gesperrt={p.locked}
        wert={{ bookingAccountId: p.item.bookingAccountId, costCentreId: p.item.costCentreId }}
        onWert={(w) => { void assign(w); }} />
    </div>
  );
}
